import React, { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  LinearProgress,
  Chip,
  IconButton,
  Tooltip,
  Button,
  useTheme,
  useMediaQuery,
  Alert,
  Collapse,
  IconButton as MuiIconButton,
  Avatar,
  AvatarGroup,
  Badge,
  CircularProgress,
} from "@mui/material";
import {
  FiUsers,
  FiTool,
  FiBell,
  FiArrowUpRight,
  FiDownload,
  FiSettings,
  FiX,
  FiMessageSquare,
  FiBarChart2,
  FiTrendingUp,
  FiClock,
  FiUserCheck,
  FiUser,
  FiBookOpen,
  FiSmartphone,
  FiSave,
  FiArrowDownLeft,
} from "react-icons/fi";
import {
  MdSupportAgent,
  MdAssignment,
  MdAccountBalanceWallet,
  MdDataUsage,
  MdOutlineCampaign,
  MdAnnouncement,
  MdWhatsapp,
  MdSmartphone,
  MdCheckCircle,
} from "react-icons/md";
import {
  RiWhatsappFill,
  RiUserFollowFill,
  RiMessage2Fill,
} from "react-icons/ri";
import { IoMdSpeedometer } from "react-icons/io";
import CardComponent from "../Components/Card.js";
import ClientOverview from "../Components/ClientOverview";
import ReminderPopup from "../Components/ReminderPopup";
import apiEndpoints from "../apiconfig";
import { BsFilePost, BsFillFileEarmarkEaselFill } from "react-icons/bs";
import { Send, Shield } from "lucide-react";
import DeliveryChartUI from "../DynamicComponents/DeliveryChartUI.js";
import { DeliveryDiningRounded, Security } from "@mui/icons-material";
import { fi } from "date-fns/locale";

const Dashboard = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const [cardData, setCardData] = useState({
    marketing: 0,
    userInitiated: 0,
    authentication: 0,
    businessInitiated: 0,
    utility: 0,
  });
  const [clientOverview, setClientOverview] = useState({
    sent: 0,
    delivered: 0,
    read: 0,
    totalAttempted: 0,
  });
  const [usage, setUsage] = useState({ used: 0, total: 1000, tier: "TIER_1" });
  const [showReminder, setShowReminder] = useState(false);
  const [token, setToken] = useState(localStorage.getItem("token") || "");
  const [user, setUser] = useState({});
  const [balance, setBalance] = useState(0);
  const [announcement, setAnnouncement] = useState([]);
  const [showAnnouncement, setShowAnnouncement] = useState(true);
  const [currentAnnouncement, setCurrentAnnouncement] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const [realTimeStats, setRealTimeStats] = useState({
    activeConversations: 12,
    pendingReplies: 5,
    todayMessages: 247,
    responseRate: 89.2,
  });
  const [loading, setLoading] = useState(true);

  // Helper function to get consistent colors for cards
  const getCardColor = (title) => {
    const colorMap = {
      Marketing: "#25D366",
      Authentication: "#FF6B35",
      Utility: "#6366F1",
      "User Initiated": "#8B5CF6",
      "Business Initiated": "#F59E0B",
    };
    return colorMap[title] || "#25D366";
  };

  useEffect(() => {
    fetchAnnouncement();
  }, []);

  // Check if user is admin and fetch announcement
  useEffect(() => {
    if (!Array.isArray(announcement) || announcement.length <= 1) return;

    const interval = setInterval(() => {
      setCurrentAnnouncement((prev) =>
        prev + 1 === announcement.length ? 0 : prev + 1,
      );
    }, 5000);

    return () => clearInterval(interval);
  }, [announcement]);
  const customerId = localStorage.getItem("token");
  const fetchAnnouncement = async () => {
    try {
      const response = await fetch(
        `${apiEndpoints.announcements}?action=customer`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );

      console.log("Announcement API status:", response.status);

      const data = await response.json();
      console.log("Announcement API response:", data);

      if (data.success && Array.isArray(data.data)) {
        const active = data.data.filter((a) => a.isActive === true);
        setAnnouncement(active);
      } else {
        setAnnouncement([]);
      }
    } catch (error) {
      console.error("Error fetching announcement:", error);
    }
  };

  useEffect(() => {
    const token = localStorage.getItem("token");

    if (!token) {
      console.error("No token found in localStorage");
      return;
    }

    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const response = await fetch(apiEndpoints.dashboard, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = await response.json();

        if (data.status === "success") {
          // ✅ User info
          setUser({
            id: data.user?.id ?? "",
            companyName: data.user?.companyName ?? "Unknown",
            email: data.user?.email ?? "Not Available",
            primary_contact_name: data.user?.primary_contact_name ?? "",
            waba_number: data.user?.waba_number ?? "",
            status: data.user?.status ?? "",
          });

          // ✅ Wallet balance
          setBalance(data.balance ?? 0);

          // ✅ Usage info
          setUsage({
            used: data.usage?.used ?? 0,
            total: data.usage?.total ?? 1000,
            tier: data.usage?.tier ?? "TIER_1",
          });

          // ✅ Card category values
          setCardData({
            marketing: data.cards?.marketing ?? 0,
            authentication: data.cards?.authentication ?? 0,
            utility: data.cards?.utility ?? 0,
            userInitiated: data.cards?.userInitiated ?? 0,
            businessInitiated: data.cards?.businessInitiated ?? 0,
          });

          // ✅ Client overview (sent, delivered, read)
          setClientOverview({
            sent: data.clientOverview?.sent ?? 0,
            delivered: data.clientOverview?.delivered ?? 0,
            read: data.clientOverview?.read ?? 0,
            totalAttempted: data.clientOverview?.totalAttempted ?? 0,
          });

          console.log("✅ Dashboard data mapped:", data);
        } else {
          console.error("Dashboard Error:", data.message);
        }
      } catch (err) {
        console.error("Dashboard Fetch Error:", err);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  const usagePercentage = (usage.used / usage.total) * 100;

  const cardsData = useMemo(
    () => ({
      Overview: [
        {
          icon: <FiMessageSquare size={28} color="#25D366" />,
          title: "Marketing",
          value: cardData.marketing,
          subtitle: "Promotional Messages",
          backgroundColor: "#FFFFFF",
          trend: "+12%",
          growth: "positive",
          onClick: () => navigate("/templates?category=marketing"),
        },
        {
          icon: <Security size={28} color="#FF6B35" />,
          title: "Authentication",
          value: cardData.authentication,
          subtitle: "OTP & Security",
          backgroundColor: "#FFFFFF",
          trend: "+8%",
          growth: "positive",
          onClick: () => navigate("/templates?category=authentication"),
        },
        {
          icon: <FiBell size={28} color="#6366F1" />,
          title: "Utility",
          value: cardData.utility,
          subtitle: "Service Updates",
          backgroundColor: "#FFFFFF",
          trend: "+5%",
          growth: "positive",
          onClick: () => navigate("/templates?category=utility"),
        },
        {
          icon: <FiArrowDownLeft size={28} color="#8B5CF6" />,
          title: "User Initiated",
          value: cardData.userInitiated,
          subtitle: "Customer Queries",
          backgroundColor: "#FFFFFF",
          trend: "+15%",
          growth: "positive",
          // onClick: () => navigate("/conversations")
        },
        {
          icon: <FiArrowUpRight size={28} color="#F59E0B" />,
          title: "Business Initiated",
          value: cardData.businessInitiated,
          subtitle: "Alerts & Notifications",
          backgroundColor: "#FFFFFF",
          trend: "+10%",
          growth: "positive",
          // onClick: () => navigate("/campaigns")
        },
      ],
    }),
    [cardData],
  );

  const realTimeCards = [
    {
      icon: <FiMessageSquare size={20} color="#25D366" />,
      title: "Total Sent",
      value: clientOverview.sent,
      subtitle: "All time messages",
      color: "#25D366",
      trend: "+2",
    },
    {
      icon: <FiClock size={20} color="#F59E0B" />,
      title: "Delivered",
      value: clientOverview.delivered,
      subtitle: "Successfully delivered",
      color: "#F59E0B",
      trend: "-1",
    },
    {
      icon: <FiBarChart2 size={20} color="#6366F1" />,
      title: "Read",
      value: clientOverview.read,
      subtitle: "Messages read",
      color: "#6366F1",
      trend: "+24",
    },
    {
      icon: <FiTrendingUp size={20} color="#10B981" />,
      title: "Delivery Rate",
      value: `${
        clientOverview.totalAttempted > 0
          ? (
              (clientOverview.delivered / clientOverview.totalAttempted) *
              100
            ).toFixed(1)
          : 0
      }%`,
      subtitle: "Success rate",
      color: "#10B981",
      trend: "+2.1%",
    },
  ];

  const quickStats = [
    {
      label: "Delivery Rate",
      value: `${
        clientOverview.totalAttempted > 0
          ? (
              (clientOverview.delivered / clientOverview.totalAttempted) *
              100
            ).toFixed(1)
          : 0
      }%`,
      change: "+2.1%",
    },
    {
      label: "Read Rate",
      value: `${
        clientOverview.totalAttempted > 0
          ? (
              (clientOverview.read / clientOverview.totalAttempted) *
              100
            ).toFixed(1)
          : 0
      }%`,
      change: "+1.5%",
    },
    {
      label: "Messages Used",
      value: `${usage.used}/${usage.total}`,
      change: `${usagePercentage.toFixed(1)}%`,
    },
  ];

  const recentActivities = [
    {
      action: "Template Approved",
      time: "2 hours ago",
      status: "success",
      icon: <MdCheckCircle size={16} />,
    },
    {
      action: `Campaign Sent to ${cardData.marketing} users`,
      time: "5 hours ago",
      status: "success",
      icon: <MdOutlineCampaign size={16} />,
    },
    {
      action: "API Limit Warning",
      time: "1 day ago",
      status: "warning",
      icon: <FiBell size={16} />,
    },
    {
      action: `Payment Received ₹${balance}`,
      time: "2 days ago",
      status: "success",
      icon: <MdAccountBalanceWallet size={16} />,
    },
  ];

  const teamMembers = [
    { name: "You", role: "Admin", active: true },
    { name: "Sarah", role: "Agent", active: true },
    { name: "Mike", role: "Agent", active: false },
    { name: "Emma", role: "Manager", active: true },
  ];

  const handleDismissAnnouncement = async () => {
    setShowAnnouncement(false);
    if (announcement?.id) {
      try {
        await fetch(`${apiEndpoints.announcements}/dismiss`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ announcementId: announcement.id }),
        });
      } catch (error) {
        console.error("Error dismissing announcement:", error);
      }
    }
  };

  return (
    <Box
      sx={{
        padding: isMobile ? "16px" : "24px",
        backgroundColor: "#F8FAFC",
        fontFamily: "'Inter', 'SF Pro Display', -apple-system, sans-serif",
        minHeight: "100vh",
        background: "linear-gradient(135deg, #F8FAFC 0%, #F1F5F9 100%)",
      }}
    >
      {loading && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(248,250,252,0.85)",
            zIndex: 3000,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <Box sx={{ textAlign: "center" }}>
            <CircularProgress
              size={48}
              thickness={4}
              sx={{ color: "#25D366", mb: 2 }}
            />

            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#475569",
              }}
            >
              Loading dashboard…
            </Typography>
          </Box>
        </Box>
      )}

      {/* Header Section */}
      <Box
        sx={{
          display: "flex",
          alignItems: isMobile ? "flex-start" : "center",
          justifyContent: "space-between",
          mb: 4,
          flexDirection: isMobile ? "column" : "row",
          gap: isMobile ? 2 : 0,
        }}
      >
        <Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, mb: 1 }}>
            <Box
              sx={{
                padding: "10px",
                backgroundColor: "#25D366",
                borderRadius: "14px",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                boxShadow: "0 4px 12px rgba(37, 211, 102, 0.3)",
              }}
            >
              <RiWhatsappFill size={28} color="#FFFFFF" />
            </Box>
            <Box>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 800,
                  color: "#0F172A",
                  letterSpacing: "-0.5px",
                  fontSize: isMobile ? "1.75rem" : "2.25rem",
                  background:
                    "linear-gradient(135deg, #0F172A 0%, #374151 100%)",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  WebkitTextFillColor: "transparent",
                }}
              >
                Business Dashboard
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#64748B",
                  fontWeight: 500,
                  display: "flex",
                  alignItems: "center",
                  gap: 2,
                  flexWrap: "wrap",
                  mt: 0.5,
                }}
              >
                Welcome back, {user?.primary_contact_name} 👋
                {/* <Chip
                  label="Professional Plan"
                  size="small"
                  sx={{
                    backgroundColor: "#25D366",
                    color: "white",
                    fontWeight: 600,
                    fontSize: "12px",
                    height: "24px",
                  }}
                /> */}
                {isAdmin && (
                  <Chip
                    label="Administrator"
                    size="small"
                    sx={{
                      backgroundColor: "#EF4444",
                      color: "white",
                      fontWeight: 600,
                      fontSize: "12px",
                      height: "24px",
                    }}
                  />
                )}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>
      {/* Admin Announcement Section */}
      {announcement.length > 0 && showAnnouncement && (
        <Collapse in={showAnnouncement}>
          <Alert
            severity={
              announcement[currentAnnouncement].type === "success"
                ? "success"
                : announcement[currentAnnouncement].type === "error"
                  ? "error"
                  : "info"
            }
            icon={<MdAnnouncement size={26} />}
            sx={{
              mb: 3,
              borderRadius: "16px",

              // Dynamic priority colors
              backgroundColor:
                announcement[currentAnnouncement].priority === "high"
                  ? "#FEE2E2" // red
                  : announcement[currentAnnouncement].priority === "medium"
                    ? "#DBEAFE" // blue
                    : "#DCFCE7", // green

              color:
                announcement[currentAnnouncement].priority === "high"
                  ? "#991B1B" // red
                  : announcement[currentAnnouncement].priority === "medium"
                    ? "#1E3A8A" // blue
                    : "#065F46", // green

              border:
                announcement[currentAnnouncement].priority === "high"
                  ? "1px solid #FCA5A5" // red
                  : announcement[currentAnnouncement].priority === "medium"
                    ? "1px solid #93C5FD" // blue
                    : "1px solid #86EFAC", // green

              boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
              "& .MuiAlert-message": { flex: 1 },
            }}
            action={
              <MuiIconButton
                aria-label="close"
                color="inherit"
                size="small"
                onClick={() => setShowAnnouncement(false)}
              >
                <FiX size={16} />
              </MuiIconButton>
            }
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                {announcement[currentAnnouncement].title}
              </Typography>

              <Typography variant="body2" sx={{ fontSize: "14px" }}>
                {announcement[currentAnnouncement].message}
              </Typography>

              <Typography
                variant="caption"
                sx={{ color: "#475569", fontSize: "12px" }}
              >
                Posted on:{" "}
                {new Date(
                  announcement[currentAnnouncement].createdAt,
                ).toLocaleDateString()}
              </Typography>
            </Box>
          </Alert>
        </Collapse>
      )}

      {/* ✅ Real-time Stats Grid — 4 cards in a single row */}
      <Box
        sx={{
          width: "100%",
          maxWidth: 1400,
          mx: "auto",
          px: 0,
          mb: 4,
        }}
      >
        <Grid
          container
          spacing={{ xs: 2, sm: 3, md: 3 }}
          justifyContent="center"
        >
          {realTimeCards.map((stat, index) => (
            <Grid item xs={12} sm={6} md={3} key={index}>
              <Card
                sx={{
                  borderRadius: "16px",
                  backgroundColor: "#FFFFFF",
                  boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                  p: 2.5,
                  border: "1px solid #F1F5F9",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                  transition: "all 0.3s ease",
                  cursor: "pointer",
                  "&:hover": {
                    transform: "translateY(-2px)",
                    boxShadow: "0 8px 25px rgba(0,0,0,0.12)",
                    borderColor: stat.color,
                  },
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    mb: 2,
                  }}
                >
                  <Box
                    sx={{
                      padding: "8px",
                      backgroundColor: `${stat.color}15`,
                      borderRadius: "10px",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    {stat.icon}
                  </Box>
                  <Chip
                    label={stat.trend}
                    size="small"
                    sx={{
                      backgroundColor: stat.trend.includes("+")
                        ? "#DCFCE7"
                        : "#FEF3C7",
                      color: stat.trend.includes("+") ? "#166534" : "#92400E",
                      fontWeight: 600,
                      fontSize: "11px",
                      height: "20px",
                    }}
                  />
                </Box>

                <Typography
                  variant="h4"
                  sx={{
                    fontWeight: 700,
                    color: "#0F172A",
                    mb: 0.5,
                    fontSize: "1.5rem",
                  }}
                >
                  {stat.value}
                </Typography>

                <Typography
                  variant="body2"
                  sx={{
                    color: "#64748B",
                    fontSize: "14px",
                    fontWeight: 500,
                  }}
                >
                  {stat.title}
                </Typography>

                <Typography
                  variant="caption"
                  sx={{
                    color: "#94A3B8",
                    fontSize: "12px",
                  }}
                >
                  {stat.subtitle}
                </Typography>
              </Card>
            </Grid>
          ))}
        </Grid>
      </Box>

      {/* Main Stats Grid */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {/* Wallet Balance */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: "20px",
              background: "linear-gradient(135deg, #25D366 0%, #128C7E 100%)",
              color: "#FFFFFF",
              boxShadow: "0px 8px 32px rgba(37, 211, 102, 0.3)",
              p: 3,
              transition: "all 0.3s ease",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0px 12px 40px rgba(37, 211, 102, 0.4)",
              },
              position: "relative",
              overflow: "hidden",
              minHeight: "200px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                top: -40,
                right: -40,
                width: 160,
                height: 160,
                backgroundColor: "rgba(255,255,255,0.1)",
                borderRadius: "50%",
              }}
            />
            <Box sx={{ position: "relative", zIndex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 2,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#FFFFFF",
                    fontSize: "16px",
                  }}
                >
                  Wallet Balance
                </Typography>
                <MdAccountBalanceWallet size={24} color="#FFFFFF" />
              </Box>
              <Typography
                variant="h3"
                sx={{
                  fontWeight: 800,
                  color: "#FFFFFF",
                  mb: 1,
                  fontSize: { xs: "2rem", md: "2.5rem" },
                }}
              >
                ₹{" "}
                {balance.toLocaleString("en-IN", {
                  minimumFractionDigits: 2,
                })}
              </Typography>
            </Box>
          </Card>
        </Grid>

        {/* Usage Statistics */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: "20px",
              backgroundColor: "#FFFFFF",
              boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
              p: 3,
              cursor: "pointer",
              transition: "all 0.3s ease",
              border: "1px solid #F1F5F9",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0px 8px 32px rgba(0,0,0,0.12)",
              },
              minHeight: "200px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 1,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#0F172A",
                    fontSize: "16px",
                  }}
                >
                  Usage Overview
                </Typography>
                <MdDataUsage size={24} color="#25D366" />
              </Box>
              <Chip
                label={`${usage.tier.replace("_", " ")}`}
                size="small"
                sx={{
                  backgroundColor:
                    usage.tier === "TIER_1"
                      ? "#FEF3C7"
                      : usage.tier === "TIER_2"
                        ? "#DBEAFE"
                        : "#DCFCE7",
                  color:
                    usage.tier === "TIER_1"
                      ? "#92400E"
                      : usage.tier === "TIER_2"
                        ? "#1E3A8A"
                        : "#166534",
                  fontWeight: 400,
                  fontSize: "9px",
                  mb: 1,
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  color: "#64748B",
                  mb: 3,
                  fontSize: "14px",
                }}
              >
                Your WhatsApp API usage for 24 hours 
              </Typography>
              <LinearProgress
                variant="determinate"
                value={usagePercentage}
                sx={{
                  height: 8,
                  borderRadius: 4,
                  mb: 2,
                  "& .MuiLinearProgress-bar": {
                    backgroundColor:
                      usagePercentage > 80 ? "#EF4444" : "#25D366",
                    borderRadius: 4,
                  },
                  backgroundColor: "#F1F5F9",
                }}
              />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 600,
                    color: "#0F172A",
                    fontSize: "14px",
                  }}
                >
                  {usage.used} / {usage.total} messages
                </Typography>
                <Typography
                  sx={{
                    fontWeight: 700,
                    color: usagePercentage > 80 ? "#EF4444" : "#25D366",
                    fontSize: "14px",
                  }}
                >
                  {usagePercentage.toFixed(1)}%
                </Typography>
              </Box>
            </Box>
          </Card>
        </Grid>

        {/* Team & Performance */}
        <Grid item xs={12} md={4}>
          <Card
            sx={{
              borderRadius: "20px",
              backgroundColor: "#FFFFFF",
              boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
              p: 3,
              cursor: "pointer",
              transition: "all 0.3s ease",
              border: "1px solid #F1F5F9",
              "&:hover": {
                transform: "translateY(-4px)",
                boxShadow: "0px 8px 32px rgba(0,0,0,0.12)",
              },
              minHeight: "200px",
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
            }}
          >
            <Box>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: "#0F172A",
                    fontSize: "16px",
                  }}
                >
                  Performance Metrics
                </Typography>
                <FiTrendingUp size={24} color="#25D366" />
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {quickStats.map((stat, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      justifyContent: "space-between",
                      alignItems: "center",
                    }}
                  >
                    <Typography
                      sx={{
                        fontSize: "14px",
                        color: "#64748B",
                        fontWeight: 500,
                      }}
                    >
                      {stat.label}
                    </Typography>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: "#0F172A",
                          fontSize: "14px",
                        }}
                      >
                        {stat.value}
                      </Typography>
                      <Chip
                        label={stat.change}
                        size="small"
                        sx={{
                          backgroundColor: stat.change.includes("+")
                            ? "#DCFCE7"
                            : "#FEF3C7",
                          color: stat.change.includes("+")
                            ? "#166534"
                            : "#92400E",
                          fontWeight: 600,
                          fontSize: "11px",
                          height: "20px",
                        }}
                      />
                    </Box>
                  </Box>
                ))}
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Main Content Grid */}
      <Grid container spacing={3}>
        <Grid item xs={12} lg={8}>
          <Card
            sx={{
              borderRadius: "20px",
              backgroundColor: "#FFFFFF",
              boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
              mb: 3,
              border: "1px solid #F1F5F9",
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: 3, pb: 2 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 3,
                }}
              >
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 700,
                    color: "#0F172A",
                    fontSize: "18px",
                    display: "flex",
                    alignItems: "center",
                    gap: 1,
                  }}
                >
                  <IoMdSpeedometer size={20} color="#25D366" />
                  Message Overview
                </Typography>
                <Button
                  endIcon={<FiArrowUpRight />}
                  sx={{
                    color: "#25D366",
                    fontWeight: 600,
                    fontSize: "14px",
                    textTransform: "none",
                    borderRadius: "10px",
                    px: 2,
                  }}
                  onClick={() => navigate("/Reports")}
                >
                  View Analytics
                </Button>
              </Box>

              {/* Fixed Message Overview Cards Grid */}
              <Grid container spacing={2}>
                {cardsData.Overview.map((card, index) => (
                  <Grid
                    item
                    xs={12}
                    sm={6}
                    md={4}
                    lg={4}
                    key={index}
                    sx={{
                      display: "flex",
                      minHeight: "140px",
                    }}
                  >
                    <Card
                      onClick={card.onClick}
                      sx={{
                        borderRadius: "16px",
                        backgroundColor: card.backgroundColor || "#FFFFFF",
                        boxShadow: "0 2px 12px rgba(0,0,0,0.08)",
                        p: 2.5,
                        border: "1px solid #F1F5F9",
                        transition: "all 0.3s ease",
                        cursor: "pointer",
                        width: "100%",
                        display: "flex",
                        flexDirection: "column",
                        justifyContent: "space-between",
                        "&:hover": {
                          transform: "translateY(-4px)",
                          boxShadow: "0 8px 25px rgba(0,0,0,0.15)",
                          borderColor: "#25D366",
                        },
                      }}
                    >
                      {/* Card Header */}
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "flex-start",
                          justifyContent: "space-between",
                          mb: 2,
                        }}
                      >
                        <Box
                          sx={{
                            padding: "10px",
                            backgroundColor: `${getCardColor(card.title)}15`,
                            borderRadius: "12px",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                          }}
                        >
                          {card.icon}
                        </Box>
                        <Chip
                          label={card.trend}
                          size="small"
                          sx={{
                            backgroundColor:
                              card.growth === "positive"
                                ? "#DCFCE7"
                                : "#FEE2E2",
                            color:
                              card.growth === "positive"
                                ? "#166534"
                                : "#DC2626",
                            fontWeight: 600,
                            fontSize: "11px",
                            height: "22px",
                          }}
                        />
                      </Box>

                      {/* Card Content */}
                      <Box sx={{ flex: 1 }}>
                        <Typography
                          variant="h4"
                          sx={{
                            fontWeight: 700,
                            color: "#0F172A",
                            mb: 1,
                            fontSize: { xs: "1.5rem", sm: "1.75rem" },
                            lineHeight: 1.2,
                          }}
                        >
                          {card.value}
                        </Typography>
                        <Typography
                          variant="h6"
                          sx={{
                            fontWeight: 600,
                            color: "#0F172A",
                            mb: 0.5,
                            fontSize: "16px",
                            lineHeight: 1.3,
                          }}
                        >
                          {card.title}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            color: "#64748B",
                            fontSize: "14px",
                            fontWeight: 500,
                            lineHeight: 1.4,
                          }}
                        >
                          {card.subtitle}
                        </Typography>
                      </Box>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            </Box>
          </Card>
        </Grid>

        <Grid item xs={12} lg={4}>
          <Card
            sx={{
              borderRadius: "20px",
              backgroundColor: "#FFFFFF",
              boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
              mb: 3,
              border: "1px solid #F1F5F9",
              overflow: "hidden",
            }}
          >
            <Box sx={{ p: 3 }}>
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 700,
                  mb: 3,
                  color: "#0F172A",
                  fontSize: "18px",
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                }}
              >
                <FiTool size={18} color="#25D366" />
                Quick Actions
              </Typography>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                {[
                  {
                    icon: <FiUsers size={20} />,
                    label: "Bulk Messaging",
                    description: "Send campaigns to multiple contacts",
                    path: "/compose-message?tab=Group",
                    color: "#25D366",
                    badge: "New",
                  },
                  {
                    icon: <FiUser size={20} />,
                    label: "Compose Message",
                    description: "Send a message to a contact",
                    path: "/compose-message",
                    color: "#6366F1",
                  },
                  {
                    icon: <FiBell size={20} />,
                    label: "Templates",
                    description: "Manage message templates",
                    path: "/templates",
                    color: "#F59E0B",
                  },
                  {
                    icon: <MdSupportAgent size={20} />,
                    label: "Support",
                    description: "Get help & documentation",
                    path: "/support",
                    color: "#8B5CF6",
                  },
                ].map((action, index) => (
                  <Box
                    key={index}
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 2,
                      p: 2,
                      backgroundColor: "#F8FAFC",
                      borderRadius: "12px",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      border: "1px solid transparent",
                      position: "relative",
                      "&:hover": {
                        backgroundColor: "#FFFFFF",
                        borderColor: action.color,
                        transform: "translateX(4px)",
                        boxShadow: `0 4px 12px ${action.color}20`,
                      },
                    }}
                    onClick={() => navigate(action.path)}
                  >
                    <Box sx={{ color: action.color }}>{action.icon}</Box>
                    <Box sx={{ flex: 1 }}>
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        <Typography
                          sx={{
                            fontWeight: 600,
                            color: "#0F172A",
                            fontSize: "14px",
                          }}
                        >
                          {action.label}
                        </Typography>
                        {action.badge && (
                          <Chip
                            label={action.badge}
                            size="small"
                            sx={{
                              backgroundColor: "#25D366",
                              color: "white",
                              fontWeight: 600,
                              fontSize: "10px",
                              height: "18px",
                            }}
                          />
                        )}
                      </Box>
                      <Typography
                        sx={{ color: "#64748B", fontSize: "12px", mt: 0.5 }}
                      >
                        {action.description}
                      </Typography>
                    </Box>
                    <FiArrowUpRight size={16} color="#64748B" />
                  </Box>
                ))}
              </Box>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {/* Full Width Support Section */}
      <Grid container spacing={3} sx={{ mt: 1 }}>
        <Grid item xs={12}>
          <Card
            sx={{
              borderRadius: "20px",
              background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
              color: "#FFFFFF",
              boxShadow: "0px 8px 32px rgba(102, 126, 234, 0.3)",
              width: "100%",
              overflow: "hidden",
              position: "relative",
            }}
          >
            <Box
              sx={{
                p: 4,
                textAlign: "center",
                display: "flex",
                flexDirection: isMobile ? "column" : "row",
                alignItems: "center",
                justifyContent: "space-between",
                gap: isMobile ? 3 : 0,
                position: "relative",
                zIndex: 1,
              }}
            >
              <Box
                sx={{
                  textAlign: isMobile ? "center" : "left",
                  flex: 1,
                }}
              >
                <Typography
                  variant="h5"
                  sx={{ fontWeight: 700, mb: 1, color: "#FFFFFF" }}
                >
                  Need Help with WhatsApp Business?
                </Typography>
                <Typography
                  variant="body1"
                  sx={{ color: "rgba(255,255,255,0.9)", fontSize: "16px" }}
                >
                  Our dedicated support team is here to help you maximize your
                  WhatsApp Business potential
                </Typography>
              </Box>
              <Button
                variant="outlined"
                size="large"
                sx={{
                  borderColor: "rgba(255,255,255,0.7)",
                  color: "#FFFFFF",
                  fontWeight: 600,
                  borderRadius: "12px",
                  px: 4,
                  py: 1.5,
                  fontSize: "16px",
                  "&:hover": {
                    borderColor: "#FFFFFF",
                    backgroundColor: "rgba(255,255,255,0.1)",
                    transform: "translateY(-2px)",
                  },
                  transition: "all 0.3s ease",
                }}
                onClick={() => navigate("/support")}
              >
                Contact Support
              </Button>
            </Box>
          </Card>
        </Grid>
      </Grid>

      {showReminder && (
        <ReminderPopup token={token} onClose={() => setShowReminder(false)} />
      )}
    </Box>
  );
};

export default Dashboard;
